import { useMemo, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format, startOfMonth, endOfMonth } from 'date-fns'
import { ArrowLeft, ChevronDown, Download, Filter, BarChart3 } from 'lucide-react'
import { getUser } from '@/api/users'
import { getDailyBreakdown, type DailyBreakdownDay } from '@/api/attendance'
import { exportAttendanceSheet } from '@/api/hrAnalytics'
import { StatusBadge, fmtTime, fmtDur } from '@/components/AttendanceTimeline'
import { cn } from '@/lib/utils'

// One pill per *real* day status this app actually tracks, plus the two
// derived flags (late / left early) — no invented categories (e.g. the
// reference's "Missing"/"Official Duty"/"Travel" have no Managition data
// behind them, so they're intentionally not here).
const STATUS_PILLS: { key: string; label: string; tone: string }[] = [
  { key: 'completed', label: 'Present', tone: 'green' },
  { key: 'overtime', label: 'Overtime', tone: 'indigo' },
  { key: 'less_hours', label: 'Short Hours', tone: 'amber' },
  { key: 'working', label: 'Working', tone: 'blue' },
  { key: 'wfh', label: 'WFH', tone: 'purple' },
  { key: 'leave', label: 'Leave', tone: 'yellow' },
  { key: 'absent', label: 'Absent', tone: 'red' },
  { key: 'holiday', label: 'Holiday', tone: 'slate' },
  { key: 'off', label: 'Day Off', tone: 'gray' },
  { key: '__late', label: 'Late Arrivals', tone: 'orange' },
  { key: '__early_left', label: 'Early Left', tone: 'pink' },
]

const PILL_TONE_CLS: Record<string, string> = {
  green: 'bg-green-100 text-green-700', indigo: 'bg-indigo-100 text-indigo-700',
  amber: 'bg-amber-100 text-amber-700', blue: 'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700', yellow: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700', slate: 'bg-slate-100 text-slate-600',
  gray: 'bg-gray-100 text-gray-600', orange: 'bg-orange-100 text-orange-700',
  pink: 'bg-pink-100 text-pink-700',
}

const EARLY_LEFT_THRESHOLD = 15

function pillCount(key: string, days: DailyBreakdownDay[]): number {
  if (key === '__late') return days.filter((d) => d.is_late).length
  if (key === '__early_left') return days.filter((d) => d.early_checkout_minutes >= EARLY_LEFT_THRESHOLD).length
  return days.filter((d) => d.status === key).length
}

function timeOfDay(iso: string | null): string | null {
  return iso ? format(new Date(iso), 'HH:mm') : null
}

function checkinStatus(d: DailyBreakdownDay): { label: string; cls: string } {
  if (!d.checkin_time) return { label: '—', cls: 'text-gray-400' }
  if (d.is_late) return { label: `Late by ${d.late_minutes}m`, cls: 'text-orange-600 font-medium' }
  return { label: 'On Time', cls: 'text-green-600 font-medium' }
}

function checkoutStatus(d: DailyBreakdownDay): { label: string; cls: string } {
  if (d.is_active) return { label: 'Active', cls: 'text-blue-600 font-medium' }
  if (!d.checkout_time) return { label: '—', cls: 'text-gray-400' }
  if (d.early_checkout_minutes >= EARLY_LEFT_THRESHOLD) return { label: `Early by ${d.early_checkout_minutes}m`, cls: 'text-pink-600 font-medium' }
  return { label: 'On Time', cls: 'text-green-600 font-medium' }
}

interface AppliedFilters {
  start: string
  end: string
  statuses: Set<string>
  checkinFrom: string
  checkinTo: string
  checkoutFrom: string
  checkoutTo: string
}

function defaultFilters(start?: string, end?: string): AppliedFilters {
  return {
    start: start || format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: end || format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    statuses: new Set(),
    checkinFrom: '', checkinTo: '', checkoutFrom: '', checkoutTo: '',
  }
}

export default function EmployeeAttendance() {
  const { userId, orgSlug } = useParams<{ userId: string; orgSlug: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [applied, setApplied] = useState<AppliedFilters>(() =>
    defaultFilters(searchParams.get('start') || undefined, searchParams.get('end') || undefined))
  const [pending, setPending] = useState<AppliedFilters>(applied)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [summaryOpen, setSummaryOpen] = useState(true)
  const [exporting, setExporting] = useState(false)

  const user = useQuery({ queryKey: ['user', userId], queryFn: () => getUser(userId!), enabled: !!userId })
  const breakdown = useQuery({
    queryKey: ['attendance-daily', userId, applied.start, applied.end],
    queryFn: () => getDailyBreakdown(userId!, applied.start, applied.end),
    enabled: !!userId,
  })

  const days = useMemo(() => breakdown.data?.days || [], [breakdown.data])

  const filteredDays = useMemo(() => {
    return [...days].reverse().filter((d) => {
      if (applied.statuses.size) {
        const matches = [...applied.statuses].some((key) => {
          if (key === '__late') return d.is_late
          if (key === '__early_left') return d.early_checkout_minutes >= EARLY_LEFT_THRESHOLD
          return d.status === key
        })
        if (!matches) return false
      }
      if (applied.checkinFrom || applied.checkinTo) {
        const t = timeOfDay(d.checkin_time)
        if (!t) return false
        if (applied.checkinFrom && t < applied.checkinFrom) return false
        if (applied.checkinTo && t > applied.checkinTo) return false
      }
      if (applied.checkoutFrom || applied.checkoutTo) {
        const t = timeOfDay(d.checkout_time)
        if (!t) return false
        if (applied.checkoutFrom && t < applied.checkoutFrom) return false
        if (applied.checkoutTo && t > applied.checkoutTo) return false
      }
      return true
    })
  }, [days, applied])

  const togglePendingStatus = (key: string) => setPending((p) => {
    const n = new Set(p.statuses)
    if (n.has(key)) n.delete(key); else n.add(key)
    return { ...p, statuses: n }
  })

  const applyFilters = () => setApplied(pending)
  const resetFilters = () => { const d = defaultFilters(); setPending(d); setApplied(d) }

  // Clicking a summary pill both toggles it as an applied filter and expands
  // the day table to just that category — no extra request, it's the same
  // already-fetched day list, just narrowed.
  const toggleSummaryPill = (key: string) => {
    setApplied((a) => {
      const n = new Set(a.statuses)
      if (n.has(key)) n.delete(key); else { n.clear(); n.add(key) }
      return { ...a, statuses: n }
    })
    setPending((p) => ({ ...p, statuses: new Set(applied.statuses.has(key) ? [] : [key]) }))
  }

  const doExport = async () => {
    if (!userId) return
    setExporting(true)
    try {
      await exportAttendanceSheet({ start: applied.start, end: applied.end, user_ids: [userId] })
    } finally { setExporting(false) }
  }

  const b = breakdown.data
  const displayName = b?.name || user.data?.name
  const initials = displayName ? displayName.split(' ').map((p: string) => p[0]).slice(0, 2).join('').toUpperCase() : '—'

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <button
        onClick={() => navigate(`/${orgSlug}/admin/analytics`)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" /> Back to HR Analytics
      </button>

      {/* Header card */}
      <div className="rounded-xl bg-purple-50/60 border border-purple-100 p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {user.data?.avatar_url ? (
            <img src={user.data.avatar_url} alt={displayName} className="h-14 w-14 rounded-full object-cover" />
          ) : (
            <span className="h-14 w-14 rounded-full bg-brand/15 text-brand text-lg font-semibold flex items-center justify-center">
              {initials}
            </span>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900">{displayName || 'Loading…'}</h1>
              {b?.employee_id && (
                <span className="rounded-full bg-brand/10 text-brand text-[11px] font-semibold px-2 py-0.5">{b.employee_id}</span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {[b?.department, b?.designation].filter(Boolean).join(' · ') || (b?.role ? b.role.replace('_', ' ') : '')}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <div className="relative h-16 w-16">
            <svg viewBox="0 0 64 64" className="-rotate-90 h-16 w-16">
              <circle cx="32" cy="32" r="27" fill="none" stroke="#e9d5ff" strokeWidth="7" />
              <circle
                cx="32" cy="32" r="27" fill="none" stroke="#7c3aed" strokeWidth="7" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 27}
                strokeDashoffset={2 * Math.PI * 27 * (1 - (breakdown.data?.attendance_rate || 0) / 100)}
                className="transition-all duration-700"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-purple-700">
              {breakdown.data ? `${breakdown.data.attendance_rate}%` : '—'}
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Attendance Score</p>
        </div>
      </div>

      {/* Advance filters */}
      <div className="rounded-xl bg-white border border-gray-100 shadow-sm">
        <button onClick={() => setFiltersOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5">
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-800"><Filter className="h-4 w-4" /> Advance Filters</span>
          <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', filtersOpen && 'rotate-180')} />
        </button>
        {filtersOpen && (
          <div className="border-t border-gray-100 p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">From</span>
                <input type="date" value={pending.start} max={pending.end}
                  onChange={(e) => setPending((p) => ({ ...p, start: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">To</span>
                <input type="date" value={pending.end} min={pending.start}
                  onChange={(e) => setPending((p) => ({ ...p, end: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">Checkin Time</span>
                <div className="flex items-center gap-2">
                  <input type="time" value={pending.checkinFrom} onChange={(e) => setPending((p) => ({ ...p, checkinFrom: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  <span className="text-gray-400">–</span>
                  <input type="time" value={pending.checkinTo} onChange={(e) => setPending((p) => ({ ...p, checkinTo: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">Checkout Time</span>
                <div className="flex items-center gap-2">
                  <input type="time" value={pending.checkoutFrom} onChange={(e) => setPending((p) => ({ ...p, checkoutFrom: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  <span className="text-gray-400">–</span>
                  <input type="time" value={pending.checkoutTo} onChange={(e) => setPending((p) => ({ ...p, checkoutTo: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>
            </div>
            <div>
              <span className="block text-xs font-medium text-gray-600 mb-2">Attendance Status</span>
              <div className="flex flex-wrap gap-2">
                {STATUS_PILLS.map((p) => (
                  <button key={p.key} onClick={() => togglePendingStatus(p.key)}
                    className={cn('rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                      pending.statuses.has(p.key) ? `${PILL_TONE_CLS[p.tone]} border-transparent` : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50')}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={resetFilters} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Reset</button>
              <button onClick={applyFilters} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark">Search</button>
            </div>
          </div>
        )}
      </div>

      {/* Attendance summary (clickable legend) */}
      <div className="rounded-xl bg-white border border-gray-100 shadow-sm">
        <button onClick={() => setSummaryOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5">
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-800"><BarChart3 className="h-4 w-4" /> Attendance Summary</span>
          <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', summaryOpen && 'rotate-180')} />
        </button>
        {summaryOpen && (
          <div className="border-t border-gray-100 p-5 flex flex-wrap gap-2">
            {STATUS_PILLS.map((p) => (
              <button
                key={p.key}
                onClick={() => toggleSummaryPill(p.key)}
                title={`Filter by ${p.label}`}
                className={cn('flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all',
                  applied.statuses.has(p.key) ? `${PILL_TONE_CLS[p.tone]} ring-2 ring-offset-1 ring-current` : PILL_TONE_CLS[p.tone])}
              >
                {p.label}
                <span className="rounded-full bg-white/70 px-1.5 font-semibold">{pillCount(p.key, days)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Day-by-day table */}
      <div className="rounded-xl bg-white border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-800">{filteredDays.length} day(s) · {applied.start} → {applied.end}</span>
          <button onClick={doExport} disabled={exporting || breakdown.isLoading}
            className="flex items-center gap-1.5 rounded-lg bg-[#1E2A3A] px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
            <Download className="h-4 w-4" />{exporting ? 'Exporting…' : 'Export XLSX'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs text-gray-500">
                <th className="px-4 py-2.5 font-semibold">Date</th>
                <th className="px-3 py-2.5 font-semibold">Time Slot</th>
                <th className="px-3 py-2.5 font-semibold">Checked In</th>
                <th className="px-3 py-2.5 font-semibold">Checkin Status</th>
                <th className="px-3 py-2.5 font-semibold">Checked Out</th>
                <th className="px-3 py-2.5 font-semibold">Checkout Status</th>
                <th className="px-3 py-2.5 text-right font-semibold">Expected Hours</th>
                <th className="px-3 py-2.5 text-right font-semibold">Worked Hours</th>
                <th className="px-3 py-2.5 text-right font-semibold">Overtime</th>
                <th className="px-3 py-2.5 text-right font-semibold">Shift Worked Hours</th>
                <th className="px-4 py-2.5 text-right font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.isLoading ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
              ) : breakdown.isError ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-red-500">Failed to load attendance.</td></tr>
              ) : filteredDays.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-gray-400">No days match the current filters.</td></tr>
              ) : filteredDays.map((d) => {
                const cin = checkinStatus(d)
                const cout = checkoutStatus(d)
                const expectedH = d.expected_minutes ? round1(d.expected_minutes / 60) : null
                const overtimeH = d.expected_minutes ? round1(Math.max(0, d.worked_minutes - d.expected_minutes) / 60) : 0
                const shiftWorkedH = round1(Math.min(d.worked_minutes, d.expected_minutes || 0) / 60)
                return (
                  <tr key={d.date} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-800 whitespace-nowrap">
                      {format(new Date(d.date + 'T00:00:00'), 'EEE, dd MMM yyyy')}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {d.expected_start && d.expected_end ? `${d.expected_start} – ${d.expected_end}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-gray-700">{fmtTime(d.checkin_time)}</td>
                    <td className={cn('px-3 py-2.5', cin.cls)}>{cin.label}</td>
                    <td className="px-3 py-2.5 text-gray-700">{d.is_active ? '—' : fmtTime(d.checkout_time)}</td>
                    <td className={cn('px-3 py-2.5', cout.cls)}>{cout.label}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{expectedH != null ? `${expectedH}h` : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700">{d.worked_hours ? fmtDur(d.worked_minutes) : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-indigo-600">{overtimeH > 0 ? `${overtimeH}h` : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{shiftWorkedH > 0 ? `${shiftWorkedH}h` : '—'}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <StatusBadge status={d.status} late={d.is_late} lateMins={d.late_minutes} leaveType={d.leave_type} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}
